// AuthContext.js
import React, { createContext, useContext, useState, useEffect, useMemo } from "react";

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const mapRoleIdFallback = (ud) => {
    const rid =
        ud?.ID_Rol ??
        ud?.ID_ROL ??
        ud?.idRol ??
        ud?.roleId ??
        ud?.ROLE_ID ??
        null;

    // soporta número o string
    const v = String(rid ?? "").trim();
    if (!v) return "";

    if (v === "1") return "GESTOR";
    if (v === "2") return "ANALISTA";
    if (v === "3") return "VISUALIZADOR";

    return "";
};

const normalizeRole = (ud) => {
    const raw =
        ud?.role ??
        ud?.ROLE ??
        ud?.ROLE_NAME ??          // ✅ backend puede mandar esto
        ud?.roleName ??
        ud?.ROL ??
        ud?.tipoUsuario ??
        ud?.TIPO_USUARIO ??
        ud?.perfil ??
        ud?.PERFIL ??
        "";

    const normalized = String(raw ?? "").trim().toUpperCase();
    if (normalized) return normalized;

    // ✅ fallback por ID_Rol numérico (viejo)
    return mapRoleIdFallback(ud);
};

const getUserId = (ud) => {
    return (
        ud?.userId ??
        ud?.USER_ID ??
        ud?.ID_USUARIO ??
        ud?._id ??
        null
    );
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userData, setUserData] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem("Token");
        let storedUserData = localStorage.getItem("userData");

        if (token && storedUserData) {
            try {
                storedUserData = JSON.parse(storedUserData);

                // Si ya estaba "flattened" lo respetamos.
                const flattenedUserData = {
                    ...(storedUserData?.user || {}),
                    ...storedUserData,
                };

                setIsAuthenticated(true);
                setUserData(flattenedUserData);
            } catch (error) {
                console.error("Error parsing userData from localStorage:", error);
                localStorage.removeItem("userData");
                localStorage.removeItem("Token");
                setIsAuthenticated(false);
                setUserData(null);
            }
        }
    }, []);

    const login = (incoming) => {
        const flattenedUserData = {
            ...(incoming?.user || {}),
            ...incoming,
        };

        setIsAuthenticated(true);
        setUserData(flattenedUserData);
        localStorage.setItem("Token", flattenedUserData.token);
        localStorage.setItem("userData", JSON.stringify(flattenedUserData));
    };

    const logout = () => {
        setIsAuthenticated(false);
        setUserData(null);
        localStorage.removeItem("Token");
        localStorage.removeItem("userData");
    };

    const computed = useMemo(() => {
        const role = normalizeRole(userData);
        const userId = getUserId(userData);

        const isViewer =
            role === "VISUALIZADOR" ||
            role === "VIEWER" ||
            role === "VISUALIZER";

        const isAnalyst =
            role === "ANALISTA" ||
            role === "ANALYST";

        return { role, userId, isViewer, isAnalyst };
    }, [userData]);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                userData,
                login,
                logout,
                ...computed,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
